import React, { useState, useEffect, useRef } from 'react';
import './ProductDetails.css';

/* ─── 1. DATA SHEET content ─── */
function DataSheet() {
  const handleDownload = () => {
    const content = `MART CMS & POS-LITE — PRODUCT DATA SHEET
==========================================
Version 3.0 | Aiprus Software Pvt. Ltd.

PRODUCT OVERVIEW
----------------
Mart CMS is a full-stack retail management portal covering product catalogues,
inventory, vendors, orders, promotions, and delivery operations.

POS-Lite is a fast, browser-based billing counter application designed for
speed at the checkout — supporting POS, quick commerce, bulk, and party orders.

KEY MODULES
-----------
Mart CMS:
  • Product Management (Categories, Variants, Pricing, Tax, SKU, Vendor Mapping)
  • Vendor Management (Suppliers, Purchase Orders, Invoices, History)
  • Inventory Management (Real-Time, Warehouse, Stock Movement, Low Stock Alerts)
  • Store Management (Configuration, Branch Management)
  • Order Management (POS, Mobile, Delivery, Reconciliation)
  • Offers & Promotions (Coupons, Discounts, Advertisements)
  • Delivery Integration (Assignment, Tracking, Proof of Delivery)
  • Refund Support

POS-Lite:
  • POS Billing & Checkout
  • Open / Close Register & Till Management
  • Quick Commerce, Bulk Order, Party Order
  • Order History
  • Barcode Scan
  • Cash, Online & COD Payment Modes
  • Business Hour Configuration

COMPANION APPS
--------------
  • Customer App — Browse, order, and track deliveries
  • Porter App   — Delivery partner order assignment & POD

COMPLIANCE & INTEGRATIONS
--------------------------
  • Payment Gateways: UPI, Cards, COD
  • ERP-ready REST API
  • Role-Based Access Control (RBAC)
  • Multi-Store & Franchise Support

CONTACT
-------
Email : info@aiprus.com
Phone : +91 84200 06452
Web   : www.aiprus.com
`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'MartCMS-POS-Lite-DataSheet.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const specs = [
    { label: 'Product Name', value: 'Mart CMS & POS-Lite' },
    { label: 'Version', value: '3.0' },
    { label: 'Vendor', value: 'Aiprus Software Pvt. Ltd.' },
    { label: 'Deployment', value: 'Cloud SaaS / On-Premise' },
    { label: 'Platform', value: 'Web (React) + REST API' },
    { label: 'Mobile Apps', value: 'Customer App + Porter App' },
    { label: 'Languages', value: 'English, Hindi (Multilingual ready)' },
    { label: 'Support', value: 'Email, Phone, On-site (Enterprise)' },
  ];

  return (
    <div className="pd-panel">
      <div className="pd-panel-header">
        <div>
          <h3 className="pd-panel-title">Product Data Sheet</h3>
          <p className="pd-panel-sub">Official product overview — suitable for procurement and vendor evaluation.</p>
        </div>
        <button className="pd-download-btn" onClick={handleDownload}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v8M5 7l3 3 3-3M2 12h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Download Data Sheet
        </button>
      </div>

      <div className="pd-datasheet-grid">
        {/* Quick facts table */}
        <div className="pd-card">
          <div className="pd-card-label">Product Identification</div>
          <table className="pd-table">
            <tbody>
              {specs.map(s => (
                <tr key={s.label}>
                  <td className="pd-table-key">{s.label}</td>
                  <td className="pd-table-val">{s.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Feature summary */}
        <div>
          <div className="pd-card" style={{ marginBottom: 16 }}>
            <div className="pd-card-label">Mart CMS — Module Summary</div>
            <div className="pd-feature-list">
              {['Product Management','Vendor Management','Inventory Management','Store & Branch Config','Order Management','Offers & Promotions','Delivery Integration','Refund Support','Analytics Dashboard'].map(f => (
                <div key={f} className="pd-feature-item">
                  <span className="pd-check">✓</span> {f}
                </div>
              ))}
            </div>
          </div>
          <div className="pd-card">
            <div className="pd-card-label">POS-Lite — Module Summary</div>
            <div className="pd-feature-list">
              {['POS Billing Counter','Register & Till Management','Quick Commerce','Bulk & Party Orders','Barcode Scan','Cash / Online / COD','Order History','Business Hour Config'].map(f => (
                <div key={f} className="pd-feature-item">
                  <span className="pd-check">✓</span> {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="pd-download-cta">
        <span className="pd-download-cta-text">📄 Download the full data sheet as a text file for offline use and procurement documentation.</span>
        <button className="pd-download-btn" onClick={handleDownload}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v8M5 7l3 3 3-3M2 12h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Download .TXT
        </button>
      </div>
    </div>
  );
}

/* ─── 2. TECH SPECS content ─── */
function TechSpecs() {
  const sections = [
    {
      heading: 'Frontend',
      rows: [
        ['Framework', 'React 18 (Functional Components + Hooks)'],
        ['State Management', 'Context API / Redux Toolkit'],
        ['UI Library', 'Custom Design System + Tailwind CSS'],
        ['Charting', 'Recharts / Chart.js'],
        ['Build Tool', 'Vite / Create React App'],
        ['Responsive', 'Mobile-first, breakpoints at 480 / 768 / 1024 / 1280px'],
      ],
    },
    {
      heading: 'Backend',
      rows: [
        ['Runtime', 'Node.js 18 LTS'],
        ['Framework', 'Express.js (RESTful API)'],
        ['Authentication', 'JWT + Refresh Token rotation'],
        ['Role Management', 'RBAC — StoreAdmin, Manager, Staff, Customer'],
        ['Real-Time', 'Socket.IO (order & inventory events)'],
        ['File Storage', 'AWS S3 / Local (configurable)'],
      ],
    },
    {
      heading: 'Database',
      rows: [
        ['Primary DB', 'PostgreSQL 15'],
        ['Caching', 'Redis 7'],
        ['Search', 'Full-text search via PostgreSQL tsvector'],
        ['ORM', 'Prisma / Sequelize'],
        ['Migrations', 'Versioned schema migrations (automated)'],
      ],
    },
    {
      heading: 'Infrastructure & Deployment',
      rows: [
        ['Hosting', 'AWS EC2 / GCP Compute / On-Premise'],
        ['Containerisation', 'Docker + Docker Compose'],
        ['Orchestration', 'Kubernetes (Enterprise tier)'],
        ['CI/CD', 'GitHub Actions / GitLab CI'],
        ['SSL/TLS', 'Let\'s Encrypt / Custom certificate'],
        ['CDN', 'CloudFront / Cloudflare'],
      ],
    },
    {
      heading: 'Integrations & APIs',
      rows: [
        ['Payment Gateways', 'Razorpay, Paytm, Cashfree, Stripe'],
        ['Delivery Partners', 'Dunzo, Shadowfax, custom webhook'],
        ['SMS / OTP', 'Twilio, MSG91'],
        ['Email', 'SendGrid, SMTP'],
        ['ERP', 'REST API export (SAP / Tally compatible)'],
        ['API Format', 'REST JSON — OpenAPI 3.0 documented'],
      ],
    },
    {
      heading: 'Security & Compliance',
      rows: [
        ['Data Encryption', 'AES-256 at rest, TLS 1.3 in transit'],
        ['Password Policy', 'Bcrypt hashing, min 8 chars, MFA optional'],
        ['Audit Logs', 'Full action logging per user per session'],
        ['GDPR', 'Data export and deletion on request'],
        ['Pen Testing', 'Quarterly OWASP Top-10 review'],
        ['Backups', 'Daily automated backups, 30-day retention'],
      ],
    },
  ];

  return (
    <div className="pd-panel">
      <div className="pd-panel-header">
        <div>
          <h3 className="pd-panel-title">Technical Specifications</h3>
          <p className="pd-panel-sub">Full technology stack and infrastructure requirements for IT and DevOps teams.</p>
        </div>
        <div className="pd-compliance-badges">
          {['REST API','Docker','PostgreSQL','Redis','React 18'].map(b => (
            <span key={b} className="pd-badge">{b}</span>
          ))}
        </div>
      </div>

      <div className="pd-specs-grid">
        {sections.map(sec => (
          <div key={sec.heading} className="pd-card">
            <div className="pd-card-label">{sec.heading}</div>
            <table className="pd-table">
              <tbody>
                {sec.rows.map(([k, v]) => (
                  <tr key={k}>
                    <td className="pd-table-key">{k}</td>
                    <td className="pd-table-val">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      <div className="pd-spec-note">
        💡 Minimum server requirement: 4 vCPU · 8 GB RAM · 100 GB SSD. Scales horizontally for enterprise load.
      </div>
    </div>
  );
}

/* ─── 3. ARCHITECTURE DIAGRAM content ─── */
function Architecture() {
  return (
    <div className="pd-panel">
      <div className="pd-panel-header">
        <div>
          <h3 className="pd-panel-title">Architecture Diagram</h3>
          <p className="pd-panel-sub">System architecture showing all layers — clients, API gateway, services, and data stores.</p>
        </div>
      </div>

      {/* Visual Architecture */}
      <div className="pd-arch-wrap">

        {/* Layer 1 — Clients */}
        <div className="pd-arch-layer">
          <div className="pd-arch-layer-label">CLIENT LAYER</div>
          <div className="pd-arch-layer-nodes">
            {[
              { icon: '🖥', label: 'Mart CMS', sub: 'Management Portal' },
              { icon: '🧾', label: 'POS-Lite', sub: 'Billing Counter' },
              { icon: '📱', label: 'Customer App', sub: 'Mobile / Web' },
              { icon: '🏍', label: 'Porter App', sub: 'Delivery Partner' },
            ].map(n => (
              <div key={n.label} className="pd-arch-node pd-arch-node-blue">
                <span className="pd-arch-node-icon">{n.icon}</span>
                <span className="pd-arch-node-label">{n.label}</span>
                <span className="pd-arch-node-sub">{n.sub}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pd-arch-arrow">↓ HTTPS / TLS 1.3</div>

        {/* Layer 2 — API Gateway */}
        <div className="pd-arch-layer">
          <div className="pd-arch-layer-label">API GATEWAY</div>
          <div className="pd-arch-layer-nodes" style={{ justifyContent: 'center' }}>
            <div className="pd-arch-node pd-arch-node-indigo" style={{ minWidth: 260 }}>
              <span className="pd-arch-node-icon">🔀</span>
              <span className="pd-arch-node-label">API Gateway / Load Balancer</span>
              <span className="pd-arch-node-sub">Auth · Rate Limiting · Routing · SSL Termination</span>
            </div>
          </div>
        </div>

        <div className="pd-arch-arrow">↓ Internal Network</div>

        {/* Layer 3 — Services */}
        <div className="pd-arch-layer">
          <div className="pd-arch-layer-label">SERVICE LAYER</div>
          <div className="pd-arch-layer-nodes">
            {[
              { icon: '📦', label: 'Product Service', sub: 'Catalogue · SKU · Variants' },
              { icon: '🗃', label: 'Inventory Service', sub: 'Stock · Warehouse · Alerts' },
              { icon: '📋', label: 'Order Service', sub: 'POS · Mobile · Delivery' },
              { icon: '💳', label: 'Payment Service', sub: 'Cash · Online · COD' },
              { icon: '🚚', label: 'Delivery Service', sub: 'Assignment · Tracking · POD' },
              { icon: '🔔', label: 'Notification Service', sub: 'SMS · Email · Push' },
            ].map(n => (
              <div key={n.label} className="pd-arch-node pd-arch-node-teal">
                <span className="pd-arch-node-icon">{n.icon}</span>
                <span className="pd-arch-node-label">{n.label}</span>
                <span className="pd-arch-node-sub">{n.sub}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="pd-arch-arrow">↓ Data Access Layer</div>

        {/* Layer 4 — Data */}
        <div className="pd-arch-layer">
          <div className="pd-arch-layer-label">DATA LAYER</div>
          <div className="pd-arch-layer-nodes" style={{ justifyContent: 'center', gap: 16 }}>
            {[
              { icon: '🐘', label: 'PostgreSQL', sub: 'Primary DB', color: 'pd-arch-node-violet' },
              { icon: '⚡', label: 'Redis', sub: 'Cache & Sessions', color: 'pd-arch-node-amber' },
              { icon: '☁️', label: 'AWS S3', sub: 'File Storage', color: 'pd-arch-node-amber' },
              { icon: '📊', label: 'Analytics Store', sub: 'Reports & Exports', color: 'pd-arch-node-violet' },
            ].map(n => (
              <div key={n.label} className={`pd-arch-node ${n.color}`}>
                <span className="pd-arch-node-icon">{n.icon}</span>
                <span className="pd-arch-node-label">{n.label}</span>
                <span className="pd-arch-node-sub">{n.sub}</span>
              </div>
            ))}
          </div>
        </div>

        {/* External integrations row */}
        <div className="pd-arch-ext-row">
          <div className="pd-arch-ext-label">EXTERNAL INTEGRATIONS</div>
          <div className="pd-arch-ext-nodes">
            {['Razorpay / Paytm','Dunzo / Shadowfax','MSG91 / Twilio','SendGrid / SMTP','ERP / Tally'].map(e => (
              <div key={e} className="pd-arch-ext-chip">{e}</div>
            ))}
          </div>
        </div>
      </div>

      <div className="pd-spec-note" style={{ marginTop: 20 }}>
        🔒 All inter-service communication uses mTLS. Database access is restricted to the service layer via VPC private subnet.
      </div>
    </div>
  );
}

/* ─── 4. CASE STUDY content ─── */
function CaseStudy() {
  const metrics = [
    { value: '68%', label: 'Reduction in billing errors', color: '#2563eb' },
    { value: '3×', label: 'Faster checkout at counter', color: '#059669' },
    { value: '40%', label: 'Less manual stock counting', color: '#7c3aed' },
    { value: '₹12L', label: 'Monthly GMV processed', color: '#d97706' },
  ];

  return (
    <div className="pd-panel">
      <div className="pd-panel-header">
        <div>
          <h3 className="pd-panel-title">Case Study</h3>
          <p className="pd-panel-sub">How a multi-branch grocery chain in Lucknow modernised operations with Mart CMS & POS-Lite.</p>
        </div>
        <span className="pd-badge pd-badge-green">Retail · Grocery · Multi-Branch</span>
      </div>

      {/* Case Study card */}
      <div className="pd-case-hero">
        <div className="pd-case-logo">🏪</div>
        <div>
          <div className="pd-case-company">FreshMart Superstore</div>
          <div className="pd-case-meta">Lucknow, Uttar Pradesh · 4 Branches · 35 Staff</div>
          <div className="pd-case-tag-row">
            <span className="pd-badge">Grocery</span>
            <span className="pd-badge">Quick Commerce</span>
            <span className="pd-badge">Delivery</span>
          </div>
        </div>
      </div>

      {/* Story */}
      <div className="pd-case-story-grid">
        <div className="pd-case-block pd-case-block-red">
          <div className="pd-case-block-title">🔴 The Challenge</div>
          <ul className="pd-case-list">
            <li>4 branches running on disconnected Excel sheets — no real-time stock visibility across locations.</li>
            <li>Billing was done via a legacy desktop POS that crashed frequently during peak hours.</li>
            <li>Delivery orders came via WhatsApp — no tracking, frequent mix-ups.</li>
            <li>Vendor invoices were managed in a physical register with no purchase order history.</li>
            <li>Owner had no visibility into which products were actually driving revenue.</li>
          </ul>
        </div>

        <div className="pd-case-block pd-case-block-blue">
          <div className="pd-case-block-title">🔵 The Solution</div>
          <ul className="pd-case-list">
            <li><strong>Mart CMS</strong> deployed as the central management portal — all 4 branches connected to one inventory.</li>
            <li><strong>POS-Lite</strong> installed on counter tablets — web-based, zero install, works even on low-end devices.</li>
            <li><strong>Customer App</strong> launched within the first month — customers could browse, order, and track delivery.</li>
            <li><strong>Porter App</strong> equipped all 6 delivery riders with real-time assignment and proof-of-delivery capture.</li>
            <li>Vendor management module connected all 3 main suppliers — POs raised in 2 clicks.</li>
          </ul>
        </div>
      </div>

      {/* Metrics */}
      <div className="pd-case-metrics">
        {metrics.map(m => (
          <div key={m.label} className="pd-case-metric">
            <div className="pd-case-metric-value" style={{ color: m.color }}>{m.value}</div>
            <div className="pd-case-metric-label">{m.label}</div>
          </div>
        ))}
      </div>

      {/* Quote */}
      <div className="pd-case-quote">
        <div className="pd-case-quote-mark">"</div>
        <div className="pd-case-quote-text">
          Before Mart CMS, I had no idea which branch was actually profitable. Within two weeks of going live, I could see sales by branch, by category, by day — on my phone. The POS-Lite counter is so fast, my staff stopped complaining about billing.
        </div>
        <div className="pd-case-quote-author">— Proprietor, FreshMart Superstore, Lucknow</div>
      </div>

      <div className="pd-spec-note">
        * Metrics based on 3-month post-implementation review. Results vary based on store size, category mix, and staff adoption.
      </div>
    </div>
  );
}

/* ─── MAIN EXPORTED COMPONENT ─── */
const TABS = [
  { id: 'datasheet',     icon: '📄', label: 'Data Sheet' },
  { id: 'techspecs',     icon: '⚙️',  label: 'Tech Specs' },
  { id: 'architecture',  icon: '🗺',  label: 'Architecture' },
  { id: 'casestudy',     icon: '📖',  label: 'Case Study' },
];

export default function ProductDetails() {
  const [activeTab, setActiveTab] = useState('datasheet');
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) e.target.classList.add('visible'); },
      { threshold: 0.05 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const renderPanel = () => {
    switch (activeTab) {
      case 'datasheet':    return <DataSheet />;
      case 'techspecs':    return <TechSpecs />;
      case 'architecture': return <Architecture />;
      case 'casestudy':    return <CaseStudy />;
      default:             return <DataSheet />;
    }
  };

  return (
    <section id="product-details" style={{ background: '#ffffff', padding: '100px 0' }}>
      <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 40px' }}>

        {/* Section header */}
        <div ref={ref} className="reveal" style={{ marginBottom: 48 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: '1.8px', textTransform: 'uppercase', color: '#2563eb', marginBottom: 18 }}>
            <span style={{ width: 18, height: 2, background: '#2563eb', borderRadius: 2, display: 'inline-block' }} />
            Product Documentation
          </div>
          <h2 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 'clamp(32px,4vw,50px)', fontWeight: 800, lineHeight: 1.08, letterSpacing: '-1.5px', color: '#0c1220', marginBottom: 14 }}>
            Everything you need to<br />evaluate &amp; implement.
          </h2>
          <p style={{ fontSize: 17, color: '#6b7280', lineHeight: 1.7, maxWidth: 560 }}>
            Data sheet, technical specs, system architecture, and real-world case studies — all in one place.
          </p>
        </div>

        {/* Tab bar */}
        <div className="pd-tab-bar">
          {TABS.map(tab => (
            <button
              key={tab.id}
              className={`pd-tab-btn ${activeTab === tab.id ? 'pd-tab-btn-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="pd-tab-icon">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Active panel */}
        <div className="pd-panel-wrap">
          {renderPanel()}
        </div>

      </div>
    </section>
  );
}